// Admin dashboard data layer. Reads the pending_submissions view, fetches
// per-module submission details, and runs approve/reject + user management
// mutations. All operations require an active admin (RLS enforces this).
//
// The pending_submissions view (migration 008) is `security_invoker = on`,
// so admins reading it transparently see every pending row across modules
// while non-admins see only their own.

import { supabase } from './supabase.js';

// Module → table mapping. The view's module column tells us which physical
// table the id lives in.
const MODULE_TABLE = {
  bibliography: 'bibliography',
  bibliography_edit: 'bibliography_edits',
  scholar: 'scholars',
  organization: 'organizations',
  teaching_material: 'teaching_materials',
  research_tool: 'research_tools',
  broader_engagement: 'broader_engagements',
  whats_new: 'whats_new',
};

// ───────────────────── Submissions queue ─────────────────────

export async function fetchPendingQueue() {
  const { data, error } = await supabase
    .from('pending_submissions')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  const items = data ?? [];

  // The view only carries a title. Enrich bibliography rows with their author
  // list so the queue can show who wrote each pending entry at a glance —
  // useful when reviewing a bulk-imported volume of many chapters.
  const bibIds = items.filter((i) => i.module === 'bibliography').map((i) => i.id);
  if (bibIds.length) {
    const { data: bibRows } = await supabase
      .from('bibliography')
      .select('id, authors')
      .in('id', bibIds);
    const authorsById = new Map((bibRows ?? []).map((r) => [r.id, r.authors ?? []]));
    for (const item of items) {
      if (item.module === 'bibliography') item.authors = authorsById.get(item.id) ?? [];
    }
  }
  return items;
}

export async function fetchPendingCount() {
  const { count, error } = await supabase
    .from('pending_submissions')
    .select('*', { count: 'exact', head: true });
  if (error) return 0;
  return count ?? 0;
}

// Fetch the full row for a queue item (and the original entry for edits).
export async function fetchSubmissionDetail({ module, id }) {
  const table = MODULE_TABLE[module];
  if (!table) throw new Error(`Unknown module: ${module}`);

  const { data: row, error } = await supabase.from(table).select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  if (!row) throw new Error('Submission not found');

  if (module === 'bibliography_edit') {
    const { data: original, error: oerr } = await supabase
      .from('bibliography')
      .select('*')
      .eq('id', row.bibliography_id)
      .maybeSingle();
    if (oerr) throw oerr;
    // Optionally fetch submitter profile (best-effort)
    const submitter = await fetchSubmitter(row.submitted_by);
    return { module, row, original, submitter };
  }

  const submitter = await fetchSubmitter(row.submitted_by);
  return { module, row, submitter };
}

async function fetchSubmitter(uid) {
  if (!uid) return null;
  const { data } = await supabase
    .from('profiles')
    .select('id, email, display_name')
    .eq('id', uid)
    .maybeSingle();
  return data ?? null;
}

// ───────────────────── Approve / reject ─────────────────────

export async function approveSubmission({ module, id, reviewerId }) {
  const reviewedAt = new Date().toISOString();

  if (module === 'bibliography_edit') {
    // Apply the suggested changes to the original bibliography row, then
    // mark the edit row as approved.
    const { data: edit, error: eerr } = await supabase
      .from('bibliography_edits')
      .select('bibliography_id, suggested_changes')
      .eq('id', id)
      .single();
    if (eerr) throw eerr;

    const { error: uerr } = await supabase
      .from('bibliography')
      .update(edit.suggested_changes)
      .eq('id', edit.bibliography_id);
    if (uerr) throw uerr;

    const { error: aerr } = await supabase
      .from('bibliography_edits')
      .update({
        status: 'approved',
        reviewed_by: reviewerId,
        reviewed_at: reviewedAt,
      })
      .eq('id', id);
    if (aerr) throw aerr;
    return;
  }

  const table = MODULE_TABLE[module];
  if (!table) throw new Error(`Unknown module: ${module}`);
  const { error } = await supabase
    .from(table)
    .update({ status: 'published', reviewed_by: reviewerId, reviewed_at: reviewedAt })
    .eq('id', id);
  if (error) throw error;
}

export async function rejectSubmission({ module, id, reviewerId, note }) {
  const reviewedAt = new Date().toISOString();
  const table = MODULE_TABLE[module];
  if (!table) throw new Error(`Unknown module: ${module}`);
  const patch = {
    status: 'rejected',
    reviewed_by: reviewerId,
    reviewed_at: reviewedAt,
    review_note: note?.trim() || null,
  };
  const { error } = await supabase.from(table).update(patch).eq('id', id);
  if (error) throw error;
}

// ───────────────────── User management ─────────────────────

export async function fetchUsers() {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, email, display_name, role, status, preferred_language, research_goals, affiliation, created_at, updated_at')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function approveUser(id) {
  const { error } = await supabase.from('profiles').update({ status: 'active' }).eq('id', id);
  if (error) throw error;
}

export async function setUserRole(id, role) {
  const { error } = await supabase.from('profiles').update({ role }).eq('id', id);
  if (error) throw error;
}

export async function deactivateUser(id) {
  const { error } = await supabase
    .from('profiles')
    .update({ status: 'deactivated' })
    .eq('id', id);
  if (error) throw error;
}

export async function reactivateUser(id) {
  const { error } = await supabase.from('profiles').update({ status: 'active' }).eq('id', id);
  if (error) throw error;
}

// ───────────────────── Helpers ─────────────────────

// Build a sorted diff for bibliography_edit detail view.
// Returns array of { field, before, after }.
export function buildEditDiff(original, edit) {
  if (!original || !edit?.suggested_changes) return [];
  const changes = edit.suggested_changes;
  const out = [];
  for (const field of Object.keys(changes)) {
    out.push({ field, before: original[field] ?? null, after: changes[field] ?? null });
  }
  return out;
}

// Pretty title for a module.
export function moduleTitle(t, module) {
  return t(`admin.modules.${module}`);
}
